/**
 * AppProviders - 최적화된 Provider 계층 구조
 * Provider들을 올바른 순서로 구성하여 불필요한 리렌더링 방지
 */
import React, { ReactNode, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TokenProvider } from './TokenContext';
import { AuthProviderAdapter as AuthProvider } from './AuthContext';
import ToastProvider from './ToastContextOptimized';
import { startTokenRefresh, stopTokenRefresh } from '../lib/supabaseClient';

// React Query 클라이언트 설정 (재사용을 위해 외부에 정의)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5분
      refetchOnWindowFocus: false,
      retry: (failureCount, error: any) => {
        // 401, 404 에러는 재시도하지 않음
        if (error?.response?.status === 401 || error?.response?.status === 404) {
          return false;
        }
        return failureCount < 3;
      },
    },
    mutations: {
      retry: 1,
    },
  },
});

interface AppProvidersProps {
  children: ReactNode;
}

/**
 * Provider 계층 구조:
 * 1. QueryClientProvider - 데이터 페칭 (최상위)
 * 2. ToastProvider - 전역 알림 (독립적)
 * 3. TokenProvider - 토큰 관리 (AuthProvider가 의존)
 * 4. AuthProvider - 인증 상태 (TokenProvider 필요)
 *
 * 이 순서는 의존성을 고려하여 설계되었습니다.
 */
export const AppProviders: React.FC<AppProvidersProps> = ({ children }) => {
  // [2026-01-06] 토큰 자동 갱신 시작
  useEffect(() => {
    // 로그인 상태일 때만 토큰 갱신 모니터링 시작
    const accessToken = localStorage.getItem('accessToken') || localStorage.getItem('auth_access_token');
    if (accessToken) {
      startTokenRefresh();
    }

    return () => {
      stopTokenRefresh();
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <TokenProvider>
          <AuthProvider>
            {children}
          </AuthProvider>
        </TokenProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
};

export default AppProviders;